import React, { useState, useEffect, useMemo } from 'react';
import { wilayahService } from '../services/wilayahService';
import type { LocationOption } from '../types';

interface LocationDropdownProps {
  level: 'province' | 'city' | 'district';
  parentId?: string;
  value: string | null;
  onChange: (value: string, label: string) => void;
  disabled?: boolean;
}

/**
 * Searchable dropdown component for Indonesian administrative regions
 * Fetches data from wilayah.id API with error handling and fallback
 */
export const LocationDropdown: React.FC<LocationDropdownProps> = ({
  level,
  parentId,
  value,
  onChange,
  disabled = false
}) => {
  const [options, setOptions] = useState<LocationOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [fallbackMode, setFallbackMode] = useState(false);
  const [manualInput, setManualInput] = useState('');

  // Fetch data when component mounts or parentId changes
  useEffect(() => {
    const fetchData = async () => {
      // Reset state
      setError(null);
      setFallbackMode(false);

      // For city and district, parentId is required
      if ((level === 'city' || level === 'district') && !parentId) {
        setOptions([]);
        return;
      }

      setLoading(true);

      try {
        let data: LocationOption[] = [];

        switch (level) {
          case 'province':
            data = await wilayahService.getProvinces();
            break;
          case 'city':
            if (parentId) {
              data = await wilayahService.getCities(parentId);
            }
            break;
          case 'district':
            if (parentId) {
              data = await wilayahService.getDistricts(parentId);
            }
            break;
        }

        setOptions(data);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load location data';
        setError(errorMessage);
        setFallbackMode(true);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [level, parentId]);

  // Filter options based on search term
  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options;
    
    const lowerSearch = searchTerm.toLowerCase();
    return options.filter(option => 
      option.name.toLowerCase().includes(lowerSearch)
    );
  }, [options, searchTerm]);

  // Get display label for selected value
  const selectedLabel = useMemo(() => {
    if (!value) return '';
    const selected = options.find(opt => opt.id === value);
    return selected ? selected.name : value;
  }, [value, options]);

  // Handle option selection
  const handleSelect = (option: LocationOption) => {
    onChange(option.id, option.name);
    setIsOpen(false);
    setSearchTerm('');
  };

  // Handle manual input in fallback mode
  const handleManualInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setManualInput(inputValue);
    onChange(inputValue, inputValue);
  };

  // Get placeholder text based on level
  const getPlaceholder = () => {
    switch (level) {
      case 'province':
        return 'Select Province';
      case 'city':
        return 'Select City/Regency';
      case 'district':
        return 'Select District';
      default:
        return 'Select...';
    }
  };

  // Render fallback text input when API is unavailable
  if (fallbackMode) {
    return (
      <div className="relative">
        <input
          type="text"
          value={manualInput || value || ''}
          onChange={handleManualInputChange}
          disabled={disabled}
          placeholder={`Enter ${level} manually`}
          className="w-full px-4 py-3 text-gray-900 bg-white border border-yellow-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
        />
        <div className="mt-1 text-xs text-yellow-600">
          {error || 'Location service unavailable. Please enter manually.'}
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Dropdown Button */}
      <button
        type="button"
        onClick={() => !disabled && !loading && setIsOpen(!isOpen)}
        disabled={disabled || loading}
        className={`
          w-full px-4 py-3 text-left text-gray-900 bg-white border border-gray-300 rounded-lg
          focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
          disabled:bg-gray-100 disabled:cursor-not-allowed
          ${!disabled && !loading ? 'hover:bg-gray-50 cursor-pointer' : ''}
        `}
      >
        <div className="flex items-center justify-between">
          <span className={selectedLabel ? 'text-gray-900' : 'text-gray-400'}>
            {loading ? 'Loading...' : selectedLabel || getPlaceholder()}
          </span>
          <svg
            className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'transform rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {/* Dropdown Menu */}
      {isOpen && !loading && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />

          {/* Menu */}
          <div className="absolute z-50 w-full mt-2 bg-white rounded-lg shadow-xl border border-gray-200 max-h-80 overflow-hidden">
            {/* Search Input */}
            <div className="p-3 border-b border-gray-200">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search..."
                className="w-full px-3 py-2 text-sm text-gray-900 bg-gray-50 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                autoFocus
              />
            </div>

            {/* Options List */}
            <div className="overflow-y-auto max-h-60">
              {filteredOptions.length === 0 ? (
                <div className="px-4 py-3 text-sm text-gray-500 text-center">
                  {searchTerm ? 'No results found' : 'No options available'}
                </div>
              ) : (
                filteredOptions.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => handleSelect(option)}
                    className={`
                      w-full px-4 py-3 text-left text-sm hover:bg-gray-100 transition-colors
                      ${option.id === value ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-900'}
                    `}
                  >
                    {option.name}
                  </button>
                ))
              )}
            </div>
          </div>
        </>
      )}

      {/* Error Message */}
      {error && !fallbackMode && (
        <div className="mt-1 text-xs text-red-600">
          {error}
        </div>
      )}
    </div>
  );
};
